import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getProducts } from '../../api'
import './SocialProofNotification.css'

/**
 * SocialProofNotification Component
 *
 * Displays floating notifications showing recent purchases from random customers
 * Matches Elessi theme behavior: https://elessi.nasatheme.com/product/imac-2017-retina-4k/
 *
 * Features:
 * - Fetches random products from API
 * - Shows notifications with product image, customer location, and time
 * - Smooth slide-in/slide-out animations with cubic-bezier easing
 * - Randomized timing to feel organic
 * - Session-based dismiss (won't show again after close)
 * - Clickable to navigate to product page
 * - Mobile responsive
 */
export default function SocialProofNotification({
    enabled = true,
    initialDelay = 5000,        // First notification after 5 seconds
    minInterval = 20000,         // Minimum 20 seconds between notifications
    maxInterval = 35000,         // Maximum 35 seconds between notifications
    displayDuration = 6000,      // Show each notification for 6 seconds
    productCount = 20            // Fetch 20 products for variety
}) {
    const [visible, setVisible] = useState(false)
    const [isExiting, setIsExiting] = useState(false)
    const [currentNotification, setCurrentNotification] = useState(null)
    const [products, setProducts] = useState([])
    const [isDismissed, setIsDismissed] = useState(false)

    const intervalRef = useRef(null)
    const timeoutRef = useRef(null)
    const exitTimeoutRef = useRef(null)
    const lastProductIndexRef = useRef(-1)

    // Check if user dismissed notifications in this session
    useEffect(() => {
        const dismissed = sessionStorage.getItem('socialProofDismissed')
        if (dismissed === 'true') {
            setIsDismissed(true)
        }
    }, [])

    // Fetch products for notifications
    useEffect(() => {
        if (!enabled || isDismissed) return

        const fetchProducts = async () => {
            try {
                const data = await getProducts({ per_page: productCount })
                if (data && data.products && data.products.length > 0) {
                    setProducts(data.products)
                }
            } catch (error) {
                console.error('Failed to fetch products for social proof:', error)
            }
        }
        fetchProducts()
    }, [enabled, isDismissed, productCount])

    // Main notification loop with organic timing
    useEffect(() => {
        if (!enabled || isDismissed || products.length === 0) return

        // Show first notification after initial delay
        timeoutRef.current = setTimeout(() => {
            showRandomNotification()
            startNotificationLoop()
        }, initialDelay)

        return () => {
            clearTimeout(timeoutRef.current)
            clearInterval(intervalRef.current)
            clearTimeout(exitTimeoutRef.current)
        }
    }, [products, enabled, isDismissed])

    /**
     * Starts the infinite notification loop with randomized intervals
     * Prevents mechanical feeling by varying time between notifications
     */
    const startNotificationLoop = () => {
        const scheduleNext = () => {
            // Random interval between min and max for organic feel
            const randomInterval = Math.random() * (maxInterval - minInterval) + minInterval

            timeoutRef.current = setTimeout(() => {
                showRandomNotification()
                scheduleNext() // Schedule next notification
            }, randomInterval)
        }

        scheduleNext()
    }

    /**
     * Generates and displays a random notification
     * Ensures no back-to-back product repetition
     */
    const showRandomNotification = () => {
        if (products.length === 0) return

        // Get random product, avoiding immediate repetition
        let randomIndex
        do {
            randomIndex = Math.floor(Math.random() * products.length)
        } while (randomIndex === lastProductIndexRef.current && products.length > 1)

        lastProductIndexRef.current = randomIndex
        const randomProduct = products[randomIndex]

        // Generate random notification data
        const notification = generateNotificationData(randomProduct)

        setCurrentNotification(notification)
        setIsExiting(false)
        setVisible(true)

        // Schedule exit animation and hide
        exitTimeoutRef.current = setTimeout(() => {
            setIsExiting(true)

            // Wait for exit animation to complete before hiding
            setTimeout(() => {
                setVisible(false)
            }, 400) // Match CSS transition duration
        }, displayDuration)
    }

    /**
     * Generates randomized notification content
     * Creates authentic-feeling purchase notifications
     */
    const generateNotificationData = (product) => {
        // Pakistani cities for localization
        const locations = [
            'Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad',
            'Multan', 'Peshawar', 'Quetta', 'Sialkot', 'Gujranwala'
        ]

        // Random time ago (5-125 minutes)
        const minutes = Math.floor(Math.random() * 120) + 5

        // Format time ago text
        let timeAgoText
        if (minutes < 60) {
            timeAgoText = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
        } else {
            const hours = Math.floor(minutes / 60)
            timeAgoText = `${hours} hour${hours !== 1 ? 's' : ''} ago`
        }

        return {
            product: product,
            location: locations[Math.floor(Math.random() * locations.length)],
            timeAgo: timeAgoText
        }
    }

    /**
     * Handles notification dismissal
     * Stores in sessionStorage to prevent showing again
     */
    const handleDismiss = () => {
        setIsExiting(true)
        setTimeout(() => {
            setVisible(false)
            setIsDismissed(true)
            sessionStorage.setItem('socialProofDismissed', 'true')
        }, 400)

        // Clear all timers
        clearTimeout(timeoutRef.current)
        clearInterval(intervalRef.current)
        clearTimeout(exitTimeoutRef.current)
    }

    // Don't render if disabled, dismissed, or no data
    if (!enabled || isDismissed || !currentNotification) return null

    // Extract product image (API returns images array)
    const productImage = currentNotification.product.images?.[0]?.src ||
                        currentNotification.product.image

    // Use slug, fallback to id if slug is missing or invalid
    const productLink = (currentNotification.product.slug && currentNotification.product.slug !== '')
        ? currentNotification.product.slug
        : String(currentNotification.product.id)

    // Validate we have a usable link
    if (!productLink) {
        console.error('Social Proof Notification: Product has no slug or ID', currentNotification.product)
        return null
    }

    return (
        <div
            className={`social-proof-notification ${visible ? 'visible' : ''} ${isExiting ? 'exiting' : ''}`}
            role="alert"
            aria-live="polite"
        >
            <Link
                to={`/products/${productLink}`}
                className="notification-link"
                onClick={() => setVisible(false)}
                aria-label={`View ${currentNotification.product.name}`}
            >
                {productImage && (
                    <div className="notification-image">
                        <img
                            src={productImage}
                            alt={currentNotification.product.name}
                            loading="lazy"
                        />
                    </div>
                )}
                <div className="notification-content">
                    <div className="notification-message">
                        Someone in <strong>{currentNotification.location}</strong> purchased
                    </div>
                    <div className="notification-product-name">
                        {currentNotification.product.name}
                    </div>
                    <div className="notification-time">
                        {currentNotification.timeAgo}
                    </div>
                </div>
            </Link>
            <button
                className="notification-close"
                onClick={handleDismiss}
                aria-label="Dismiss notification"
                type="button"
            >
                ×
            </button>
        </div>
    )
}
